import React, { useEffect, useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../services/supabaseClient';
import {
  Container,
  Table,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  InputGroup,
  InputGroupText,
  UncontrolledTooltip,
  ButtonGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Pagination,
  PaginationItem,
  PaginationLink,
  Progress,
  Alert
} from 'reactstrap';
import './newmenores.scss';

// Cache global para almacenar los datos
let globalDataCache = {
  data: null,
  timestamp: null,
  isLoading: false
};

const CACHE_DURATION = 60 * 60 * 1000; // 60 minutos en milisegundos

const NewMenores = ({ user }) => {
  // Estados principales
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Estados para filtros
  const [departamentosSeleccionados, setDepartamentosSeleccionados] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroDescripcion, setFiltroDescripcion] = useState('');
  const [filtroEntidad, setFiltroEntidad] = useState('');
  const [filtroCodigoContratacion, setFiltroCodigoContratacion] = useState('');
  const [todosDepartamentos, setTodosDepartamentos] = useState(false);
  
  // Estados para dropdowns
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchDepartamento, setSearchDepartamento] = useState('');
  
  // Estados para ordenamiento y modal
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Estados para tooltips de descripción
  const [hoveredDescription, setHoveredDescription] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Estados para responsividad
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Referencias
  const dropdownRef = useRef(null);
  const hasInitialized = useRef(false);

  // Detectar dispositivos móviles y tablets
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      
      // En móvil, mostrar menos elementos por página por defecto
      if (width <= 768 && itemsPerPage > 10) {
        setItemsPerPage(10);
      }
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, [itemsPerPage]);

  // Función para verificar si el cache es válido
  const isCacheValid = () => {
    if (!globalDataCache.data || !globalDataCache.timestamp) return false;
    const now = Date.now();
    return (now - globalDataCache.timestamp) < CACHE_DURATION;
  };

  // Función para cargar desde cache o API
  const loadDataIfNeeded = async () => {
    if (isCacheValid() && !globalDataCache.isLoading) {
      console.log('✅ Usando datos desde cache');
      setData(globalDataCache.data);
      setDataLoaded(true);
      return;
    }

    if (globalDataCache.isLoading) {
      console.log('⏳ Datos ya se están cargando...');
      if (globalDataCache.data) {
        setData(globalDataCache.data);
        setDataLoaded(true);
      }
      return;
    }

    await loadData();
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      loadDataIfNeeded();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadData = async (forceRefresh = false) => {
    if (globalDataCache.isLoading && !forceRefresh) {
      console.log('⏸️ Carga ya en progreso, saltando...');
      return;
    }

    setLoading(true);
    setRefreshing(true);
    setError(null);
    globalDataCache.isLoading = true;
    
    try {
      console.log('🔄 Cargando datos...');
      
      const { data: contratos, error } = await supabase
        .from('contratos_menores')
        .select('*')
        .eq('convocatorias_hoy', 1);

      if (error) {
        throw error;
      }

      const formateado = contratos.map((row) => ({
        'COD CONTRATACIÓN': row.cod_contratacion,
        TIPO: row.tipo,
        DPTO: row.departamento,
        PROVINCIA: row.provincia,
        DISTRITO: row.distrito,
        ENTIDAD: row.entidad,
        DESCRIPCIÓN: row.descripcion,
        'INICIO COTIZACIÓN': row.inicio_cotizacion,
        'FIN COTIZACIÓN': row.fin_cotizacion,
        'FECHA PUBLICACIÓN': row.fecha_publicacion,
        'URL_TDR': row.url_publico_tdr,
      }));

      const ordenado = formateado.sort((a, b) => a.DPTO.localeCompare(b.DPTO));
      
      globalDataCache.data = ordenado;
      globalDataCache.timestamp = Date.now();
      
      setData(ordenado);
      setDataLoaded(true);
      
      console.log('✅ Datos cargados exitosamente:', ordenado.length, 'contratos');
      
    } catch (error) {
      console.error('❌ Error al cargar datos de Supabase:', error);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      globalDataCache.isLoading = false;
    }
  };

  const refreshData = () => {
    console.log('🔄 Refrescando datos manualmente...');
    loadData(true);
  };

  // Obtener valores únicos para filtros
  const departamentosUnicos = useMemo(() => {
    return Array.from(new Set(data.map((item) => item.DPTO))).sort();
  }, [data]);

  const tiposUnicos = useMemo(() => {
    return Array.from(new Set(data.map((item) => item.TIPO))).sort();
  }, [data]);

  // Filtrar departamentos basado en búsqueda
  const departamentosFiltrados = useMemo(() => {
    if (!searchDepartamento.trim()) return departamentosUnicos;
    return departamentosUnicos.filter(dept => 
      dept.toLowerCase().includes(searchDepartamento.toLowerCase())
    );
  }, [departamentosUnicos, searchDepartamento]);

  // Datos filtrados
  const dataFiltrada = useMemo(() => {
    let filtered = data.filter((row) => {
      const coincideDpto =
        todosDepartamentos ||
        departamentosSeleccionados.length === 0 ||
        departamentosSeleccionados.includes(row.DPTO);

      const coincideDescripcion =
        filtroDescripcion.trim() === '' ||
        (row['DESCRIPCIÓN'] || '').toLowerCase().includes(filtroDescripcion.toLowerCase());

      const coincideTipo =
        filtroTipo === '' || (row.TIPO || '').toLowerCase() === filtroTipo.toLowerCase();

      const coincideEntidad =
        filtroEntidad.trim() === '' ||
        (row.ENTIDAD || '').toLowerCase().includes(filtroEntidad.toLowerCase());

      const coincideCodigo =
        filtroCodigoContratacion.trim() === '' ||
        (row['COD CONTRATACIÓN'] || '').toLowerCase().includes(filtroCodigoContratacion.toLowerCase());

      return coincideDpto && coincideDescripcion && coincideTipo && coincideEntidad && coincideCodigo;
    });

    const parseTextDate = (dateString) => {
      if (!dateString || dateString.trim() === '' || dateString === '-') {
        return new Date(0);
      }
      
      const cleanDate = dateString.trim();
      
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleanDate)) {
        const [day, month, year] = cleanDate.split('/');
        return new Date(year, month - 1, day);
      }
      
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(cleanDate)) {
        const [day, month, year] = cleanDate.split('-');
        return new Date(year, month - 1, day);
      }
      
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(cleanDate)) {
        return new Date(cleanDate);
      }
      
      const parsed = new Date(cleanDate);
      return isNaN(parsed.getTime()) ? new Date(0) : parsed;
    };

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (sortConfig.key.includes('FECHA') || sortConfig.key.includes('COTIZACIÓN')) {
          const aDate = parseTextDate(aValue);
          const bDate = parseTextDate(bValue);
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (aStr < bStr) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [data, todosDepartamentos, departamentosSeleccionados, filtroDescripcion, 
      filtroTipo, filtroEntidad, filtroCodigoContratacion, sortConfig]);

  // Datos paginados
  const dataPaginada = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return dataFiltrada.slice(start, start + itemsPerPage);
  }, [dataFiltrada, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(dataFiltrada.length / itemsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setDepartamentosSeleccionados([]);
    setFiltroTipo('');
    setFiltroDescripcion('');
    setFiltroEntidad('');
    setFiltroCodigoContratacion('');
    setTodosDepartamentos(false);
    setSortConfig({ key: null, direction: 'asc' });
    setSearchDepartamento('');
    setCurrentPage(1);
  };

  const exportData = () => {
    try {
      console.log('🚀 Iniciando exportación...');
      
      if (dataFiltrada.length === 0) {
        alert('No hay datos para exportar. Ajusta los filtros de búsqueda.');
        return;
      }

      const excelData = dataFiltrada.map(row => ({
        'Código Contratación': row['COD CONTRATACIÓN'] || '',
        'Tipo': row.TIPO || '',
        'Departamento': row.DPTO || '',
        'Provincia': row.PROVINCIA || '',
        'Distrito': row.DISTRITO || '',
        'Entidad': row.ENTIDAD || '',
        'Descripción': row.DESCRIPCIÓN || '',
        'Fecha Publicación': row['FECHA PUBLICACIÓN'] || '',
        'Inicio Cotización': row['INICIO COTIZACIÓN'] || '',
        'Fin Cotización': row['FIN COTIZACIÓN'] || '',
        'URL TDR': row['URL_TDR'] || ''
      }));

      console.log('📊 Datos preparados:', excelData.length, 'filas');

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 40 }, { wch: 50 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 30 }
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Contratos Menores');

      const fileName = `contratos_menores_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      console.log(`✅ Excel exportado exitosamente: ${fileName}`);
      alert(`✅ Archivo Excel descargado: ${fileName}`);
      
    } catch (error) {
      console.error('❌ Error al exportar Excel:', error);
      alert(`❌ Error al exportar el archivo Excel: ${error.message}`);
    }
  };

  const openModal = (contract) => {
    setSelectedContract(contract);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedContract(null);
    setModalOpen(false);
  };

  const openPDF = (url) => {
    if (url && url.trim() !== '') {
      const pdfUrl = convertGoogleDriveUrl(url);
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('No hay URL de TDR disponible para este contrato');
    }
  };

  const convertGoogleDriveUrl = (url) => {
    if (url.includes('drive.google.com')) {
      let fileId = '';
      
      if (url.includes('/file/d/')) {
        fileId = url.split('/file/d/')[1].split('/')[0];
      } else if (url.includes('id=')) {
        fileId = url.split('id=')[1].split('&')[0];
      }
      
      if (fileId) {
        return `https://drive.google.com/file/d/${fileId}/view`;
      }
    }
    
    return url;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString.trim() === '') return '-';
    
    try {
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString.trim())) {
        return dateString.trim();
      }
      
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString.trim())) {
        return dateString.trim().replace(/-/g, '/');
      }
      
      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString.trim())) {
        const [year, month, day] = dateString.trim().split('-');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      
      if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(dateString.trim())) {
        const [year, month, day] = dateString.trim().split('/');
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      }
      
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-PE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      
      return dateString.trim();
      
    } catch (error) {
      return dateString || '-';
    }
  };

  const getTypeColor = (tipo) => {
    const colors = {
      'BIEN': 'primary',
      'SERVICIO': 'success',
      'OBRA': 'warning',
      'CONSULTORÍA': 'info',
      'BIENES': 'primary',
      'SERVICIOS': 'success',
      'OBRAS': 'warning'
    };
    return colors[tipo?.toUpperCase()] || 'secondary';
  };

  // Función para obtener columnas que se muestran en la tabla
  const getTableColumns = () => {
    if (dataFiltrada.length === 0) return [];
    const allColumns = Object.keys(dataFiltrada[0]).filter(key => key !== 'URL_TDR');
    
    // En móvil, mostrar solo las columnas más importantes
    if (isMobile) {
      return ['COD CONTRATACIÓN', 'TIPO', 'ENTIDAD', 'DESCRIPCIÓN'];
    }
    
    // En tablet, mostrar más columnas
    if (isTablet) {
      return ['COD CONTRATACIÓN', 'TIPO', 'DPTO', 'ENTIDAD', 'DESCRIPCIÓN', 'FIN COTIZACIÓN'];
    }
    
    // En desktop, mostrar todas las columnas
    return allColumns;
  };

  // Funciones para manejar tooltips de descripción
  const handleMouseEnterDescription = (event, description) => {
    if (isMobile) return; // No mostrar tooltips en móvil
    
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setHoveredDescription(description);
  };

  const handleMouseLeaveDescription = () => {
    setHoveredDescription(null);
  };

  // Manejo de selección de departamentos
  const toggleDepartamento = (departamento, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setTodosDepartamentos(false);
    setDepartamentosSeleccionados(prev => {
      if (prev.includes(departamento)) {
        return prev.filter(d => d !== departamento);
      } else {
        return [...prev, departamento];
      }
    });
  };

  const seleccionarTodos = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setTodosDepartamentos(true);
    setDepartamentosSeleccionados([]);
  };

  const limpiarSeleccion = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setTodosDepartamentos(false);
    setDepartamentosSeleccionados([]);
  };

  // Generar texto para el botón del dropdown
  const getDropdownButtonText = () => {
    if (todosDepartamentos) {
      return isMobile ? `✅ Todos (${departamentosUnicos.length})` : `✅ Todos los departamentos (${departamentosUnicos.length})`;
    }
    
    if (departamentosSeleccionados.length === 0) {
      return isMobile ? '📍 Todos' : '📍 Todos los departamentos';
    }
    
    if (departamentosSeleccionados.length === 1) {
      return isMobile ? `📍 ${departamentosSeleccionados[0].substring(0, 8)}...` : `📍 ${departamentosSeleccionados[0]}`;
    }
    
    return isMobile ? `📍 ${departamentosSeleccionados.length} depts` : `📍 ${departamentosSeleccionados.length} departamentos seleccionados`;
  };

  // Componente de paginación mejorado y responsive
  const PaginationComponent = () => {
    const getPageNumbers = () => {
      const pages = [];
      const maxPagesToShow = isMobile ? 3 : 5;
      
      if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage + 1 < maxPagesToShow) {
          startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
      }
      
      return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
      <div className="modern-pagination-wrapper">
        <div className="pagination-info">
          <span className="pagination-text">
            {isMobile ? 
              `${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, dataFiltrada.length)} de ${dataFiltrada.length}` :
              `Mostrando ${((currentPage - 1) * itemsPerPage) + 1} a ${Math.min(currentPage * itemsPerPage, dataFiltrada.length)} de ${dataFiltrada.length} contratos`
            }
          </span>
          <div className="items-per-page-selector">
            <Label className="items-label">{isMobile ? '' : 'Mostrar:'}</Label>
            <Input
              type="select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Input>
          </div>
        </div>
        
        <Pagination className="modern-pagination">
          <PaginationItem disabled={currentPage === 1} className="pagination-item">
            <PaginationLink 
              previous 
              onClick={() => setCurrentPage(currentPage - 1)}
              className="pagination-link pagination-arrow"
            />
          </PaginationItem>
          
          {pageNumbers.map(page => (
            <PaginationItem key={page} active={page === currentPage} className="pagination-item">
              <PaginationLink 
                onClick={() => setCurrentPage(page)}
                className={`pagination-link ${page === currentPage ? 'active' : ''}`}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}
          
          <PaginationItem disabled={currentPage === totalPages} className="pagination-item">
            <PaginationLink 
              next 
              onClick={() => setCurrentPage(currentPage + 1)}
              className="pagination-link pagination-arrow"
            />
          </PaginationItem>
        </Pagination>
      </div>
    );
  };

  return (
    <div className={`new-menores-container ${isMobile ? 'mobile' : ''} ${isTablet ? 'tablet' : ''}`}>
      {/* Header Superior Responsive */}
      <div className="hero-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={12} lg={8}>
              <div className="hero-content">
                <div className="hero-icon">📋</div>
                <div className="hero-text">
                  <h1 className="hero-title">
                    {isMobile ? 'Contratos Menores' : 'Contratos Menores'}
                  </h1>
                  <p className="hero-subtitle">
                    {isMobile ? 'Convocatorias del día' : 'Convocatorias del día'} • 
                    <span className="hero-badge">
                      {dataFiltrada.length} {isMobile ? 'encontradas' : 'convocatorias encontradas'}
                    </span>
                  </p>
                </div>
              </div>
            </Col>
            <Col xs={12} lg={4} className={`${isMobile ? 'text-center mt-3' : 'text-end'}`}>
              <div className="hero-actions">
                <Button
                  className="action-btn refresh-btn"
                  onClick={refreshData}
                  disabled={refreshing}
                  size={isMobile ? 'sm' : 'md'}
                  id="refreshBtn"
                >
                  {refreshing ? <Spinner size="sm" className="me-2" /> : '🔄'}
                  {refreshing ? (isMobile ? 'Actualiza...' : 'Actualizando...') : (isMobile ? 'Actualizar' : 'Actualizar')}
                </Button>
                <UncontrolledTooltip target="refreshBtn">
                  Forzar recarga de datos desde Supabase
                </UncontrolledTooltip>
                
                <Button
                  className="action-btn export-btn"
                  onClick={exportData}
                  disabled={dataFiltrada.length === 0}
                  size={isMobile ? 'sm' : 'md'}
                  id="exportBtn"
                >
                  📥 {isMobile ? 'Excel' : 'Exportar Excel'}
                </Button>
                <UncontrolledTooltip target="exportBtn">
                  Descargar datos filtrados en formato Excel
                </UncontrolledTooltip>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="main-content">
        {/* Alertas de error */}
        {error && (
          <Alert color="danger" className="modern-alert mb-4">
            <h6 className="alert-heading">⚠️ Error de carga</h6>
            {error}
          </Alert>
        )}

        {/* Barra de progreso durante la carga */}
        {loading && (
          <Card className="loading-card mb-4">
            <CardBody>
              <div className="loading-content">
                <small className="loading-text">Cargando datos...</small>
                <Progress animated value={100} className="loading-progress" />
              </div>
            </CardBody>
          </Card>
        )}

        {/* Mensaje cuando no hay datos pero no está cargando */}
        {!loading && data.length === 0 && !error && (
          <Card className="empty-data-card mb-4">
            <CardBody className="text-center">
              <div className="empty-data-content">
                <div className="empty-data-icon">📭</div>
                <h5>No hay procesos publicados el día de hoy</h5>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Solo mostrar filtros y tabla si hay datos */}
        {data.length > 0 && (
          <>
            {/* Botón para mostrar/ocultar filtros en móvil */}
            {isMobile && (
              <div className="mobile-filters-toggle mb-3">
                <Button
                  color="primary"
                  outline
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-100 filters-toggle-btn"
                >
                  🔍 {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                  <span className="ms-2">{showFilters ? '▲' : '▼'}</span>
                </Button>
              </div>
            )}

            {/* Filtros Responsivos */}
            <Card className={`filters-card mb-4 ${isMobile && !showFilters ? 'd-none' : ''}`}>
              <CardBody>
                <div className="filters-header">
                  <h5 className="filters-title">
                    <span className="filters-icon">🔍</span>
                    {isMobile ? 'Filtros' : 'Filtros de Búsqueda Avanzada'}
                  </h5>
                  {!isMobile && (
                    <Button
                      className="clear-filters-btn"
                      size="sm"onClick={clearFilters}
                    >
                      🗑️ Limpiar Filtros
                    </Button>
                  )}
                </div>

                <Row className="gy-3 gy-md-4">
                  {/* Departamento Responsive */}
                  <Col xs={12} md={6} lg={3}>
                    <FormGroup>
                      <Label className="filter-label">
                        <span className="filter-icon">📍</span>
                        Departamento
                      </Label>
                      <div ref={dropdownRef} className={`custom-dropdown-wrapper ${dropdownOpen ? 'dropdown-open' : ''}`}>
                        <Dropdown 
                          isOpen={dropdownOpen} 
                          toggle={() => setDropdownOpen(!dropdownOpen)}
                          className="w-100"
                        >
                          <DropdownToggle 
                            caret 
                            className="custom-dropdown-toggle"
                            size={isMobile ? 'sm' : 'md'}
                          >
                            <span className="dropdown-text">
                              {getDropdownButtonText()}
                            </span>
                          </DropdownToggle>
                          <DropdownMenu className={`custom-dropdown-menu ${isMobile ? 'mobile' : ''}`}>
                            {/* Buscador interno */}
                            <div className="dropdown-search-section">
                              <Input
                                type="text"
                                placeholder="🔍 Buscar departamento..."
                                value={searchDepartamento}
                                onChange={(e) => setSearchDepartamento(e.target.value)}
                                className="dropdown-search"
                                size={isMobile ? 'sm' : 'md'}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            
                            {/* Opciones de control */}
                            <div className="dropdown-control-section">
                              <div className="dropdown-controls">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="control-btn select-all-btn"
                                  onClick={(e) => seleccionarTodos(e)}
                                >
                                  ✅ {isMobile ? 'Todos' : 'Seleccionar Todos'}
                                </Button>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="control-btn clear-btn"
                                  onClick={(e) => limpiarSeleccion(e)}
                                >
                                  🗑️ Limpiar
                                </Button>
                              </div>
                            </div>

                            {/* Lista de departamentos */}
                            <div className="dropdown-items-section">
                              {departamentosFiltrados.length > 0 ? (
                                departamentosFiltrados.map((departamento) => (
                                  <DropdownItem
                                    key={departamento}
                                    className="custom-dropdown-item"
                                    onClick={(e) => toggleDepartamento(departamento, e)}
                                    toggle={false}
                                  >
                                    <span className="item-text">{departamento}</span>
                                    {(departamentosSeleccionados.includes(departamento) || todosDepartamentos) && (
                                      <span className="item-check">✓</span>
                                    )}
                                  </DropdownItem>
                                ))
                              ) : (
                                <DropdownItem disabled className="no-results-item">
                                  <span className="no-results-text">No se encontraron departamentos</span>
                                </DropdownItem>
                              )}
                            </div>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </FormGroup>
                  </Col>

                  {/* Tipo de Contrato */}
                  <Col xs={12} md={6} lg={3}>
                    <FormGroup>
                      <Label className="filter-label">
                        <span className="filter-icon">🏷️</span>
                        {isMobile ? 'Tipo' : 'Tipo de Contrato'}
                      </Label>
                      <Input
                        type="select"
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                        className="filter-input"
                        size={isMobile ? 'sm' : 'md'}
                      >
                        <option value="">{isMobile ? '📋 Todos' : '📋 Todos los tipos'}</option>
                        {tiposUnicos.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {tipo}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  {/* Entidad */}
                  <Col xs={12} md={6} lg={3}>
                    <FormGroup>
                      <Label className="filter-label">
                        <span className="filter-icon">🏢</span>
                        Entidad
                      </Label>
                      <Input
                        type="text"
                        placeholder={isMobile ? "🔍 Buscar entidad..." : "🔍 Buscar por entidad..."}
                        value={filtroEntidad}
                        onChange={(e) => setFiltroEntidad(e.target.value)}
                        className="filter-input"
                        size={isMobile ? 'sm' : 'md'}
                      />
                    </FormGroup>
                  </Col>

                  {/* Código de Contratación */}
                  <Col xs={12} md={6} lg={3}>
                    <FormGroup>
                      <Label className="filter-label">
                        <span className="filter-icon">🔢</span>
                        {isMobile ? 'Código' : 'Código de Contratación'}
                      </Label>
                      <Input
                        type="text"
                        placeholder={isMobile ? "🔍 Código..." : "🔍 Buscar por código..."}
                        value={filtroCodigoContratacion}
                        onChange={(e) => setFiltroCodigoContratacion(e.target.value)}
                        className="filter-input"
                        size={isMobile ? 'sm' : 'md'}
                      />
                    </FormGroup>
                  </Col>

                  {/* Descripción del Contrato */}
                  <Col xs={12}>
                    <FormGroup>
                      <Label className="filter-label">
                        <span className="filter-icon">📝</span>
                        {isMobile ? 'Descripción' : 'Descripción del Contrato'}
                      </Label>
                      <Input
                        type="text"
                        placeholder={isMobile ? "🔍 Buscar en descripción..." : "🔍 Buscar en la descripción del contrato..."}
                        value={filtroDescripcion}
                        onChange={(e) => setFiltroDescripcion(e.target.value)}
                        className="filter-input"
                        size={isMobile ? 'sm' : 'md'}
                      />
                    </FormGroup>
                  </Col>

                  {/* Botón limpiar filtros para móvil */}
                  {isMobile && (
                    <Col xs={12}>
                      <Button
                        className="clear-filters-btn w-100"
                        size="sm"
                        onClick={clearFilters}
                        outline
                      >
                        🗑️ Limpiar Todos los Filtros
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>

            {/* Tabla Responsiva */}
            <Card className="table-card">
              <CardBody className="p-0">
                {/* Header de la tabla */}
                <div className="table-header">
                  <div className="table-header-content">
                    <h5 className="table-title">
                      <span className="table-icon">📊</span>
                      {isMobile ? 'Resultados' : 'Resultados de la Búsqueda'}
                    </h5>
                    <Badge className="results-badge">
                      {dataFiltrada.length} {isMobile ? 'encontrados' : 'contratos encontrados'}
                    </Badge>
                  </div>
                </div>

                {dataFiltrada.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📭</div>
                    <h4 className="empty-title">No se encontraron contratos</h4>
                    <p className="empty-text">{isMobile ? 'Ajusta los filtros' : 'Intenta ajustar los filtros de búsqueda'}</p>
                  </div>
                ) : (
                  <>
                    {isMobile ? (
                      /* Vista de tarjetas para móvil */
                      <div className="mobile-cards-container">
                        {dataPaginada.map((row, index) => (
                          <Card key={index} className="mobile-contract-card">
                            <CardBody className="p-3">
                              <div className="mobile-card-header">
                                <div className="mobile-card-code">
                                  <small className="text-muted">Código:</small>
                                  <strong>{row['COD CONTRATACIÓN'] || '-'}</strong>
                                </div>
                                <Badge
                                  color={getTypeColor(row.TIPO)}
                                  className="mobile-type-badge"
                                  pill
                                >
                                  {row.TIPO || '-'}
                                </Badge>
                              </div>
                              
                              <div className="mobile-card-content">
                                <div className="mobile-card-entity">
                                  <small className="text-muted">Entidad:</small>
                                  <div className="entity-text">{row.ENTIDAD || '-'}</div>
                                </div>
                                
                                <div className="mobile-card-description">
                                  <small className="text-muted">Descripción:</small>
                                  <div className="description-text">
                                    {row['DESCRIPCIÓN'] ? 
                                      (row['DESCRIPCIÓN'].length > 100 ? 
                                        `${row['DESCRIPCIÓN'].substring(0, 100)}...` : 
                                        row['DESCRIPCIÓN']
                                      ) : '-'
                                    }
                                  </div>
                                </div>
                                
                                {row['FIN COTIZACIÓN'] && (
                                  <div className="mobile-card-date">
                                    <small className="text-muted">Fin Cotización:</small>
                                    <div className="date-text">{row['FIN COTIZACIÓN']}</div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="mobile-card-actions">
                                <Button
                                  size="sm"
                                  color="primary"
                                  onClick={() => openModal(row)}
                                  className="mobile-action-btn"
                                >
                                  Ver Detalles
                                </Button>
                                <Button
                                  size="sm"
                                  color="success"
                                  onClick={() => openPDF(row['URL_TDR'])}
                                  disabled={!row['URL_TDR'] || row['URL_TDR'].trim() === ''}
                                  className="mobile-action-btn"
                                >
                                  📄 RTM
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      /* Vista de tabla para desktop y tablet */
                      <div className="modern-table-wrapper">
                        <Table className="modern-table" responsive>
                          <thead className="table-header-row">
                            <tr>
                              {getTableColumns().map((column) => (
                                <th
                                  key={column}
                                  className="table-header-cell"
                                  onClick={() => handleSort(column)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="header-content">
                                    <span className={column === 'COD CONTRATACIÓN' ? 'header-text-prominent' : 'header-text'}>
                                      {isMobile ? column.split(' ')[0] : column}
                                    </span>
                                    <span className="sort-icon">
                                      {getSortIcon(column)}
                                    </span>
                                  </div>
                                </th>
                              ))}
                              <th className="table-header-cell">
                                <div className="header-content">
                                  <span className="header-text">ACCIONES</span>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataPaginada.map((row, index) => (
                              <tr key={index} className="table-row">
                                {getTableColumns().map((column) => (
                                  <td key={column} className="table-cell">
                                    {column === 'TIPO' ? (
                                      <Badge
                                        color={getTypeColor(row[column])}
                                        className="type-badge"
                                      >
                                        {row[column] || '-'}
                                      </Badge>
                                    ) : column === 'DESCRIPCIÓN' ? (
  <div className="description-cell" style={{ 
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    minWidth: '300px',
    maxWidth: '500px'
  }}>
    {row[column] || '-'}
  </div>
                                    ) : column.includes('FECHA') || column.includes('COTIZACIÓN') ? (
                                      <div className="date-cell">
                                        {row[column] && row[column] !== '-' ? (
                                          <>
                                            <div className="date-main">
                                              {row[column].substring(0, 10)}
                                            </div>
                                            {row[column].length > 10 && !isTablet && (
                                              <div className="date-time">
                                                {row[column].substring(10)}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <span className="date-empty">-</span>
                                        )}
                                      </div>
                                    ) : column === 'ENTIDAD' ? (
  <span className="cell-content" style={{ 
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    display: 'block',
    minWidth: '250px',
    maxWidth: '400px'
  }}>
    {row[column] || '-'}
  </span>
                                    ) : (
                                      <span className="cell-content">{row[column] || '-'}</span>
                                    )}
                                  </td>
                                ))}
                                <td className="table-cell actions-cell">
                                  <div className="action-buttons">
                                    <Button
                                      size="sm"
                                      onClick={() => openModal(row)}
                                      className="action-btn view-btn"
                                    >
                                      {isTablet ? 'Ver' : 'Ver'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => openPDF(row['URL_TDR'])}
                                      disabled={!row['URL_TDR'] || row['URL_TDR'].trim() === ''}
                                      className="action-btn tdr-btn"
                                    >
                                      📄 RTM
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* Paginación Responsiva */}
                    <div className="table-footer">
                      <PaginationComponent />
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </Container>

      {/* Modal de Detalles Responsivo */}
      <Modal 
        isOpen={modalOpen} 
        toggle={closeModal} 
        size={isMobile ? 'sm' : 'lg'}
        className="modern-modal"
        fullscreen={isMobile}
      >
        <ModalHeader 
          toggle={closeModal}
          className="modal-header-custom"
        >
          <div className="modal-header-content">
            <span className="modal-icon">📋</span>
            <div className="modal-title-section">
              <h4 className="modal-title" style={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}>
                {isMobile ? 'Detalles' : 'Detalles del Contrato'}
              </h4>
              <p className="modal-subtitle" style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                {isMobile ? 'Información completa' : 'Información completa de la convocatoria'}
              </p>
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody className="modal-body-custom">
          {selectedContract && (
            <div className="modal-content-sections">
              {/* Información principal */}
              <div className="info-section">
                <Row className={`${isMobile ? 'gy-2' : 'gy-3'}`}>
                  <Col xs={12} md={6}>
                    <div className="info-card">
                      <Label className="info-label">
                        <span className="info-icon">🔢</span>
                        {isMobile ? 'Código' : 'Código de Contratación'}
                      </Label>
                      <div className="info-value primary">
                        {selectedContract['COD CONTRATACIÓN'] || 'No disponible'}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="info-card">
                      <Label className="info-label">
                        <span className="info-icon">🏷️</span>
                        {isMobile ? 'Tipo' : 'Tipo de Contrato'}
                      </Label>
                      <Badge
                        color={getTypeColor(selectedContract.TIPO)}
                        className="info-badge"
                      >
                        {selectedContract.TIPO || 'No especificado'}
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Ubicación */}
              <div className="info-section">
                <h6 className="section-title">
                  <span className="section-icon">📍</span>
                  {isMobile ? 'Ubicación' : 'Ubicación Geográfica'}
                </h6>
                <Row className={`${isMobile ? 'gy-2' : 'gy-3'}`}>
                  <Col xs={12} md={4}>
                    <div className="info-card small">
                      <Label className="info-label-small">Departamento</Label>
                      <div className="info-value-small">{selectedContract.DPTO || 'No disponible'}</div>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="info-card small">
                      <Label className="info-label-small">Provincia</Label>
                      <div className="info-value-small">{selectedContract.PROVINCIA || 'No disponible'}</div>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="info-card small">
                      <Label className="info-label-small">Distrito</Label>
                      <div className="info-value-small">{selectedContract.DISTRITO || 'No disponible'}</div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Entidad */}
              <div className="info-section">
                <h6 className="section-title">
                  <span className="section-icon">🏢</span>
                  {isMobile ? 'Entidad' : 'Entidad Convocante'}
                </h6>
                <div className="info-card">
                  <div className="info-value large">
                    {selectedContract.ENTIDAD || 'No disponible'}
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="info-section">
                <h6 className="section-title">
                  <span className="section-icon">📝</span>
                  {isMobile ? 'Descripción' : 'Descripción del Contrato'}
                </h6>
                <div className="info-card">
                  <div className="description-content">
                    {selectedContract['DESCRIPCIÓN'] || 'No disponible'}
                  </div>
                </div>
              </div>

              {/* Fechas importantes */}
              <div className="info-section">
                <h6 className="section-title">
                  <span className="section-icon">📅</span>
                  {isMobile ? 'Fechas' : 'Cronograma de Fechas'}
                </h6>
                <Row className={`${isMobile ? 'gy-2' : 'gy-3'}`}>
                  <Col xs={12} md={4}>
                    <div className="info-card small info">
                      <Label className="info-label-small">{isMobile ? 'Publicación' : 'Fecha Publicación'}</Label>
                      <div className="info-value-small">
                        {selectedContract['FECHA PUBLICACIÓN']}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="info-card small success">
                      <Label className="info-label-small">{isMobile ? 'Inicio' : 'Inicio Cotización'}</Label>
                      <div className="info-value-small">
                        {selectedContract['INICIO COTIZACIÓN']}
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <div className="info-card small warning">
                      <Label className="info-label-small">{isMobile ? 'Fin' : 'Fin Cotización'}</Label>
                      <div className="info-value-small">
                        {selectedContract['FIN COTIZACIÓN']}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Documento TDR */}
              {selectedContract['URL_TDR'] && selectedContract['URL_TDR'].trim() !== '' && (
                <div className="info-section">
                  <h6 className="section-title">
                    <span className="section-icon">📄</span>
                    {isMobile ? 'Documentos' : 'Documentos Técnicos'}
                  </h6>
                  <div className="tdr-section">
                    <Button
                      color="success"
                      onClick={() => openPDF(selectedContract['URL_TDR'])}
                      className="tdr-download-btn"
                      size={isMobile ? 'sm' : 'md'}
                      block={isMobile}
                    >
                      📄 {isMobile ? 'Abrir RTM' : 'Abrir Requerimiento'}
                    </Button>
                    <div className="tdr-help-text">
                      {isMobile ? 'Toca para abrir el documento' : 'Haz clic para abrir el documento en una nueva ventana'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        
        <ModalFooter className="modal-footer-custom">
          <div className={`modal-footer-buttons ${isMobile ? 'mobile' : ''}`}>
            <Button
              color="secondary"
              onClick={closeModal}
              className="modal-btn secondary"
              size={isMobile ? 'sm' : 'md'}
            >
              Cerrar
            </Button>
            {selectedContract && selectedContract['URL_TDR'] && selectedContract['URL_TDR'].trim() !== '' && (
              <Button
                color="primary"
                onClick={() => openPDF(selectedContract['URL_TDR'])}
                className="modal-btn primary"
                size={isMobile ? 'sm' : 'md'}
              >
                📄 {isMobile ? 'Abrir RTM' : 'Abrir RTM'}
              </Button>
            )}
          </div>
        </ModalFooter>
      </Modal>

      {/* CSS adicional para responsividad */}
      <style jsx global>{`
        /* Estilos base responsivos */
        .new-menores-container.mobile {
          padding: 0;
        }
        
        .new-menores-container.mobile .hero-header {
          padding: 1rem 0;
        }
        
        .new-menores-container.mobile .hero-title {
          font-size: 1.5rem;
        }
        
        .new-menores-container.mobile .hero-subtitle {
          font-size: 0.9rem;
        }
        
        .new-menores-container.mobile .hero-badge {
          font-size: 0.8rem;
        }
        
        /* Filtros móviles */
        .mobile-filters-toggle {
          margin: 0 1rem;
        }
        
        .filters-toggle-btn {
          border-radius: 8px;
          font-weight: 600;
        }
        
        /* Tarjetas móviles */
        .mobile-cards-container {
          padding: 1rem;
          gap: 1rem;
          display: flex;
          flex-direction: column;
        }
        
        .mobile-contract-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border: none;
          transition: transform 0.2s ease;
        }
        
        .mobile-contract-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e9ecef;
        }
        
        .mobile-card-code {
          flex: 1;
        }
        
        .mobile-card-code small {
          display: block;
          margin-bottom: 0.25rem;
        }
        
        .mobile-card-code strong {
          font-size: 0.9rem;
          color: #495057;
        }
        
        .mobile-type-badge {
          font-size: 0.7rem;
          padding: 0.25rem 0.5rem;
        }
        
        .mobile-card-content {
          margin-bottom: 1rem;
        }
        
        .mobile-card-entity,
        .mobile-card-description,
        .mobile-card-date {
          margin-bottom: 0.75rem;
        }
        
        .mobile-card-entity small,
        .mobile-card-description small,
        .mobile-card-date small {
          display: block;
          margin-bottom: 0.25rem;
          font-weight: 600;
        }
        
        .entity-text,
        .description-text,
        .date-text {
          font-size: 0.85rem;
          color: #495057;
          line-height: 1.4;
        }
        
        .mobile-card-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .mobile-action-btn {
          flex: 1;
          font-size: 0.8rem;
          padding: 0.5rem;
          border-radius: 6px;
        }
        
        /* Dropdown móvil */
        .custom-dropdown-menu.mobile {
          max-height: 70vh;
          overflow-y: auto;
        }
        
        /* Paginación móvil */
        .new-menores-container.mobile .pagination-info {
          flex-direction: column;
          gap: 0.5rem;
          align-items: center;
        }
        
        .new-menores-container.mobile .pagination-text {
          font-size: 0.8rem;
        }
        
        .new-menores-container.mobile .modern-pagination {
          justify-content: center;
        }
        
        /* Modal móvil */
        .new-menores-container.mobile .modal-header-content {
          align-items: center;
        }
        
        .new-menores-container.mobile .modal-title-section {
          text-align: center;
        }
        
        .new-menores-container.mobile .info-section {
          margin-bottom: 1.5rem;
        }
        
        .new-menores-container.mobile .section-title {
          font-size: 1rem;
          margin-bottom: 0.75rem;
        }
        
        .new-menores-container.mobile .info-label {
          font-size: 0.8rem;
        }
        
        .new-menores-container.mobile .info-value {
          font-size: 0.9rem;
        }
        
        .new-menores-container.mobile .info-label-small {
          font-size: 0.75rem;
        }
        
        .new-menores-container.mobile .info-value-small {
          font-size: 0.8rem;
        }
        
        .modal-footer-buttons.mobile {
          display: flex;
          gap: 0.5rem;
          width: 100%;
        }
        
        .modal-footer-buttons.mobile .modal-btn {
          flex: 1;
        }
        
        /* Responsividad tablet */
        .new-menores-container.tablet .description-cell,
        .new-menores-container.tablet .entity-text {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        /* Ajustes generales para pantallas pequeñas */
        @media (max-width: 768px) {
          .container-fluid {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
          
          .table-responsive {border-radius: 8px;
            overflow-x: auto;
          }
          
          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .action-btn {
            font-size: 0.7rem;
            padding: 0.25rem 0.5rem;
          }
          
          .hero-actions {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .action-btn.refresh-btn,
          .action-btn.export-btn {
            width: 100%;
            justify-content: center;
          }
        }
        
        /* Ajustes para tablets */
        @media (min-width: 769px) and (max-width: 1024px) {
          .modern-table th,
          .modern-table td {
            padding: 0.5rem;
            font-size: 0.85rem;
          }
          
          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }
          
          .action-btn {
            font-size: 0.75rem;
            padding: 0.3rem 0.6rem;
          }
        }
        
        /* Mejoras de accesibilidad */
        @media (prefers-reduced-motion: reduce) {
          .mobile-contract-card {
            transition: none;
          }
          
          .mobile-contract-card:hover {
            transform: none;
          }
        }
        
        /* Mejoras para pantallas de alta densidad */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .mobile-type-badge,
          .mobile-action-btn {
            border-width: 0.5px;
          }
        }
        
        /* Estados de focus mejorados para móvil */
        .mobile-action-btn:focus,
        .filters-toggle-btn:focus {
          outline: 2px solid #007bff;
          outline-offset: 2px;
        }
        
        /* Animaciones suaves para transiciones */
        .mobile-cards-container,
        .custom-dropdown-menu.mobile {
          animation: fadeInUp 0.3s ease-out;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Optimizaciones de rendimiento */
        .mobile-contract-card {
          will-change: transform;
        }
        
        .mobile-cards-container {
          contain: layout style;
        }
        
        /* Mejoras de contraste para accesibilidad */
        .mobile-card-code strong {
          color: #212529;
        }
        
        .entity-text,
        .description-text,
        .date-text {
          color: #212529;
        }
        
        /* Espaciado mejorado en móvil */
        .new-menores-container.mobile .main-content {
          padding-top: 0;
        }
        
        .new-menores-container.mobile .filters-card {
          margin: 0 0.5rem 1rem 0.5rem;
          border-radius: 12px;
        }
        
        .new-menores-container.mobile .table-card {
          margin: 0 0.5rem;
          border-radius: 12px;
        }
        
        /* Mejoras para el modal en móvil */
        .new-menores-container.mobile .modal-content {
          height: 100vh;
          border-radius: 0;
        }
        
        .new-menores-container.mobile .modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
        }
        
        .new-menores-container.mobile .modal-footer {
          padding: 1rem;
          border-top: 1px solid #dee2e6;
        }
        
        /* Estados de loading mejorados */
        .loading-progress {
          height: 4px;
        }
        
        .new-menores-container.mobile .loading-text {
          font-size: 0.85rem;
        }
        
        /* Mejoras para el dropdown en móvil */
        .new-menores-container.mobile .custom-dropdown-toggle {
          font-size: 0.85rem;
          padding: 0.5rem 0.75rem;
        }
        
        .new-menores-container.mobile .dropdown-search {
          font-size: 0.85rem;
        }
        
        .new-menores-container.mobile .custom-dropdown-item {
          padding: 0.75rem 1rem;
          font-size: 0.85rem;
        }
        
        /* Optimizaciones finales */
        .new-menores-container.mobile * {
          box-sizing: border-box;
        }
        
        .new-menores-container.mobile img {
          max-width: 100%;
          height: auto;
        }
          /* SOLUCIÓN PARA EVITAR TEXTO CORTADO EN TABLA */
.modern-table .table-cell {
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  max-width: none !important;
}

.modern-table .description-cell {
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  max-width: none !important;
  min-width: 250px;
  line-height: 1.4;
}

.modern-table .cell-content {
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  display: block;
  line-height: 1.4;
}

/* Asegurar que las columnas de texto largo se muestren completas */
.modern-table td {
  white-space: normal !important;
  word-wrap: break-word !important;
  vertical-align: top;
  padding: 12px !important;
}

/* Específicamente para entidad y descripción */
.modern-table td:has(.description-cell),
.modern-table td:has(.cell-content) {
  max-width: 400px;
  min-width: 200px;
}
  /* SOLUCIÓN PARA EVITAR TEXTO CORTADO EN TABLA - SOLO DESKTOP */
@media (min-width: 769px) {
  .modern-table .table-cell {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }

  .modern-table .description-cell {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    min-width: 300px !important;
    max-width: 500px !important;
    line-height: 1.5;
    padding: 12px;
  }

  .modern-table .cell-content {
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    display: block !important;
    line-height: 1.5;
  }

  /* Asegurar que las columnas de texto largo se muestren completas */
  .modern-table td {
    white-space: normal !important;
    word-wrap: break-word !important;
    vertical-align: top !important;
    padding: 12px !important;
  }

  /* ENTIDAD */
  .modern-table td:has(.cell-content) {
    min-width: 250px;
    max-width: 400px;
  }

  /* DESCRIPCIÓN */
  .modern-table td:has(.description-cell) {
    min-width: 300px;
    max-width: 500px;
  }
}

/* En móvil mantener el comportamiento actual (con puntos suspensivos) */
@media (max-width: 768px) {
  .mobile-card-description .description-text,
  .mobile-card-entity .entity-text {
    /* Mantener como está - ya tiene el cortado con ... */
  }
}
      `}</style>
    </div>
  );
};

export default NewMenores;